import { CSSProperties, ReactNode } from 'react';
import { Stack, TextareaAutosize, styled } from '@mui/material';
import { Controller, Control, FieldValues } from 'react-hook-form';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';
import { colors } from 'theme/mui/colors';

export interface InputProps<T extends FieldValues = FieldValues> {
  name: string;
  control: Control<T>;
  label: string;
  subLabel?: { [key: string]: string | number };
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  readonly?: boolean;
  rows?: number;
  maxRows?: number;
  extraContent?: ReactNode;
}

export const TextAreaInputField = <T extends FieldValues = FieldValues>(props: InputProps<T>) => {
  const {
    name,
    control,
    label,
    subLabel,
    required = false,
    placeholder,
    disabled = false,
    readonly = false,
    style,
    rows = 5,
    maxRows = 15,
    extraContent,
  } = props;
  const { t } = useLocalization();

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <TextFormLabel label={label} subLabel={subLabel} required={required} />
        {extraContent}
      </Stack>
      <Controller
        name={name as any}
        control={control}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <>
            <StyledTextarea
              value={value || ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={placeholder ? t(placeholder) : ''}
              disabled={disabled}
              readOnly={readonly}
              minRows={rows}
              maxRows={maxRows}
              style={{
                ...style,
                borderColor: error ? colors.red[500] : '',
                resize: 'vertical',
                width: '100%',
                padding: '8px',
              }}
            />
            <TextFormError error={error} />
          </>
        )}
      />
    </Stack>
  );
};

const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
  borderColor: `${colors.base.level40}`,
  borderRadius: 8,
  padding: '1rem',
  fontFamily: 'inherit',
  width: '100%',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    // borderColor: colors.red[500],
  },
  '&::placeholder': {
    color: colors.base.level60,
    opacity: 0.7,
    fontWeight: 400,
  },
}));
